// Admin-only fetch wrapper. Lives completely separate from lib/api.ts
// so admin tokens and user tokens never share module state. If an
// admin is logged in, the user-side token is untouched (and vice
// versa) — they can't cross-contaminate each other.

import { ApiError } from '../../lib/api';

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: { code: string; message: string; details?: unknown } };
type ApiResp<T> = ApiOk<T> | ApiErr;

let adminAccessToken: string | null = null;
export const setAdminAccessToken = (t: string | null) => { adminAccessToken = t; };
export const getAdminAccessToken = () => adminAccessToken;

let refreshing: Promise<boolean> | null = null;
async function refresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await fetch('/api/admin/auth/refresh', { method: 'POST', credentials: 'include' });
      const j = (await r.json()) as ApiResp<{ accessToken: string }>;
      if (!r.ok || !j.ok) return false;
      adminAccessToken = j.data.accessToken;
      return true;
    } catch { return false; }
    finally { refreshing = null; }
  })();
  return refreshing;
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!(init.body instanceof FormData) && init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (adminAccessToken) headers.set('authorization', `Bearer ${adminAccessToken}`);
  const res = await fetch(`/api${path}`, { ...init, headers, credentials: 'include' });
  const body = (await res.json().catch(() => ({}))) as ApiResp<T>;
  if (res.status === 401 && retry && adminAccessToken) {
    if (await refresh()) return request<T>(path, init, false);
  }
  if (!res.ok || !('ok' in body) || !body.ok) {
    const err = (body as ApiErr).error || { code: 'http_error', message: `HTTP ${res.status}` };
    throw new ApiError(err.code, err.message, res.status, err.details);
  }
  return body.data;
}

function withConfirm(headers: Record<string, string> | undefined, confirmToken?: string) {
  if (!confirmToken) return headers;
  return { ...(headers || {}), 'X-Confirm-Token': confirmToken };
}

export const adminApi = {
  get:  <T>(p: string) => request<T>(p),
  post: <T>(p: string, body?: unknown, confirmToken?: string) =>
    request<T>(p, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers: withConfirm(undefined, confirmToken),
    }),
  put:  <T>(p: string, body?: unknown, confirmToken?: string) =>
    request<T>(p, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers: withConfirm(undefined, confirmToken),
    }),
  patch:<T>(p: string, body?: unknown, confirmToken?: string) =>
    request<T>(p, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      headers: withConfirm(undefined, confirmToken),
    }),
  del:  <T>(p: string, confirmToken?: string) =>
    request<T>(p, { method: 'DELETE', headers: withConfirm(undefined, confirmToken) }),
  postForm: <T>(p: string, fd: FormData) => request<T>(p, { method: 'POST', body: fd }),
  tryRefresh: refresh,
};

export type AdminUser = {
  id: number;
  // Phase R5 — Unique Agent ID (public-shareable). Generated server-
  // side at agent-create / migration-060 backfill, never reassigned.
  // For role='admin' the column is NULL; only agents carry a UAID.
  // Surfaced via AGENT_PUBLIC_COLS so this field arrives on every
  // /admin/auth/me + /admin/agents/:id response.
  uaid: string | null;
  username: string;
  email: string;
  avatar_url: string | null;
  // Phase 3: role-aware. The platform owner is role='admin'; agents
  // promoted via /admin/agents are role='agent'.
  role: 'admin' | 'agent';
  parent_admin_id: number | null;
  // permissions JSON — the capability matrix (none|view|edit per key).
  // Empty {} = no permissions (admin role doesn't use this; ignored).
  permissions: Record<string, 'none' | 'view' | 'edit'>;
  // commission_terms JSON — what cuts a agent earns. Admin role
  // leaves this null/empty.
  commission_terms: Record<string, unknown>;
  status: 'active' | 'suspended';
  // 2026-05-18 — Identity masking. Structured display name + birthdate
  // surfaced via /admin/auth/me so the UI can compose display_name
  // without an extra round-trip. Birthdate is agent-only; the admins
  // table doesn't carry one.
  first_name: string | null;
  last_name:  string | null;
  birthdate:  string | null;
  // Numeric depth in the chain (admin promotes L1; each parent-agent
  // creates L<parent.level+1>). Admins are not levelled — null for them.
  level?: number | null;
  // Phase R4 — locator opt-out. When false, the geographic locator
  // skips this agent at every cascade level (L1-L5). Direct
  // referrals + manually-assigned players are unaffected.
  // Comes back as 0 / 1 from MySQL TINYINT(1); both falsy/truthy
  // semantics work for `if (!agent.is_accepting_players)` checks.
  is_accepting_players: 0 | 1 | boolean;
  /** Per-field self-edit map. Each field one of {'none','view','edit'}. NULL → fall back to identity_locked boolean. */
  identity_locked_fields?: { username?: 'none'|'view'|'edit'; email?: 'none'|'view'|'edit'; avatar?: 'none'|'view'|'edit'; password?: 'none'|'view'|'edit' } | null;
  last_login_at: string | null;
  last_login_ip: string | null;
  created_at: string;
  updated_at: string;

  // ── Phase R4 (Hyper-Local Mesh): structured geo on the agent row.
  // Surfaced through AGENT_PUBLIC_COLS so every endpoint that
  // returns an agent (admin/auth/me, GET /admin/agents/:id, …)
  // ships these. The agent-frontend's forced-pop-up gates on
  // `latitude == null || longitude == null` to decide whether to
  // demand precise GPS at login. Note: agents do NOT carry
  // pincode_id — only users do (per migration 058 Track B).
  // MySQL DECIMAL columns come back as strings on some driver
  // configs and numbers on others, so the union accommodates both.
  latitude:       string | number | null;
  longitude:      string | number | null;
  state_id:       number | null;
  district_id:    number | null;
  area_id:        number | null;
  nation:         string | null;
  geo_updated_at: string | null;

  // Phase 10 follow-up (2026-05-12): per-agent UI language. `language`
  // is the actively selected interface language (defaults 'en' until
  // the agent picks something via the LanguageSwitcher in the top
  // bar). `secondary_language` mirrors the user-side schema for
  // future dual-language render — currently unused by the UI but
  // surfaced here so callers can read/write it through /admin/auth/me
  // and PATCH /admin/me-agent/language without a type cast.
  language?:           string;
  secondary_language?: string | null;
};
